import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Gift, Copy, Check, Users, Zap, Link2 } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';

const ReferralPage = () => {
  const { user, token, API } = useAuth();
  const [stats, setStats] = useState(null);
  const [copied, setCopied] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/referral/stats`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setStats(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token, API]);

  const referralCode = stats?.referral_code || user?.referral_code || '';
  const referralLink = `${window.location.origin}/auth?mode=register&ref=${referralCode}`;

  const handleCopy = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
    toast.success('Copied to clipboard!');
  };

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="w-10 h-10 border-2 border-primary border-t-transparent rounded-full animate-spin" />
    </div>
  );

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Header */}
      <div className="glass-card rounded-2xl p-6 border border-border relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-purple-500/5 to-pink-500/5 -z-0" />
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center">
              <Gift className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="font-bold font-outfit text-foreground text-lg">Referral Program</h2>
              <p className="text-xs text-muted-foreground">Invite friends & earn 5,000 bonus credits each</p>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-3 mb-6">
            <div className="rounded-xl bg-secondary/60 p-4 text-center">
              <Users className="w-5 h-5 text-primary mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">{stats?.total_referrals || 0}</p>
              <p className="text-xs text-muted-foreground">Referrals</p>
            </div>
            <div className="rounded-xl bg-secondary/60 p-4 text-center">
              <Zap className="w-5 h-5 text-green-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">{(stats?.total_bonus_earned || 0).toLocaleString()}</p>
              <p className="text-xs text-muted-foreground">Bonus Earned</p>
            </div>
            <div className="rounded-xl bg-secondary/60 p-4 text-center">
              <Gift className="w-5 h-5 text-purple-500 mx-auto mb-1" />
              <p className="text-2xl font-bold text-foreground">5,000</p>
              <p className="text-xs text-muted-foreground">Per Referral</p>
            </div>
          </div>

          {/* Referral Code */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-foreground">Your Referral Code</label>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 rounded-xl bg-background border border-border text-foreground font-mono text-sm tracking-wider">
                {referralCode}
              </div>
              <button
                data-testid="copy-referral-code"
                onClick={() => handleCopy(referralCode)}
                className="px-4 py-3 rounded-xl bg-primary text-primary-foreground hover:opacity-90 transition-opacity flex items-center gap-1.5"
              >
                {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              </button>
            </div>

            <label className="text-sm font-medium text-foreground">Referral Link</label>
            <div className="flex gap-2">
              <div className="flex-1 px-4 py-3 rounded-xl bg-background border border-border text-muted-foreground text-xs truncate">
                {referralLink}
              </div>
              <button
                data-testid="copy-referral-link"
                onClick={() => handleCopy(referralLink)}
                className="px-4 py-3 rounded-xl bg-secondary text-foreground hover:bg-secondary/70 transition-colors flex items-center gap-1.5"
              >
                <Link2 className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* How It Works */}
      <div className="glass-card rounded-2xl p-6 border border-border">
        <h3 className="font-semibold font-outfit text-foreground mb-4">How It Works</h3>
        <div className="space-y-3">
          {[
            { step: "1", text: "Share your unique referral code or link with friends" },
            { step: "2", text: "They sign up using your code and get 5,000 extra credits" },
            { step: "3", text: "You also receive 5,000 bonus credits automatically" },
          ].map((item, i) => (
            <div key={i} className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-pink-600 flex items-center justify-center text-white font-bold text-sm flex-shrink-0">
                {item.step}
              </div>
              <p className="text-sm text-foreground">{item.text}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Recent Referrals */}
      {stats?.referrals?.length > 0 && (
        <div className="glass-card rounded-2xl p-6 border border-border">
          <h3 className="font-semibold font-outfit text-foreground mb-4">Recent Referrals</h3>
          <div className="space-y-2">
            {stats.referrals.map((r, i) => (
              <div key={i} className="flex items-center gap-3 p-3 rounded-xl bg-secondary/50">
                <div className="w-8 h-8 rounded-full bg-gradient-to-br from-green-500 to-emerald-600 flex items-center justify-center text-white text-xs font-bold">
                  {r.referred_name?.[0] || '?'}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground truncate">{r.referred_name}</p>
                  <p className="text-xs text-muted-foreground">{new Date(r.created_at).toLocaleDateString()}</p>
                </div>
                <span className="text-xs font-medium text-green-500">+{r.bonus.toLocaleString()} credits</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default ReferralPage;
